import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AddUserToProjectDialogAtom } from '../atoms/addUserToProjectDialogAtom';
import { clientsPaginationAtom } from '../atoms/clientsPaginationAtom';
import { Multiselect } from './Multiselect';
import { useClientsQuery, useUpdateProjectUsersMutation } from '../lib/queries';
import { User } from '../types';
import { QUERY_KEYS } from '../lib/constants';
import { PATHS } from '../lib/paths';

export function AddUserToProjectDialog() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [state, setState] = useAtom(AddUserToProjectDialogAtom);
  const dialog = useRef<HTMLDialogElement | null>(null);
  const [clientsPagination, setClientsPagination] = useAtom(clientsPaginationAtom);
  const { watch, setValue, formState, reset, handleSubmit } = useForm<{
    clients: User[];
  }>({
    mode: 'all',
    resolver: zodResolver(
      z.object({
        clients: z.custom<User>().array().min(1, 'error.required'),
      }),
    ),
  });

  const clients = watch('clients', []) as User[];
  const clientsQuery = useClientsQuery(clientsPagination);
  const updateProjectUsersMutation = useUpdateProjectUsersMutation();
  const client = useQueryClient();

  const handleClose = () => {
    setState({ isOpen: false, project: undefined });
    dialog.current?.close();
  };

  const onSubmit = (data: { clients: User[] }) => {
    if (state.project) {
      updateProjectUsersMutation.mutate(
        {
          pid: state.project?.id,
          users: [
            ...new Set([...state.project.users.map((u) => u.id), ...data.clients.map((u) => u.id)]),
          ],
        },
        {
          onSuccess: () => {
            toast.success(t('project.addUsersDialog.success'));
            handleClose();
            client.invalidateQueries({
              queryKey: [QUERY_KEYS.PROJECT],
            });
          },
          onError: (error) => {
            const err = error as Error;
            navigate(PATHS.errorPath(err.message));
          },
        },
      );
    }
  };

  useEffect(
    () => {
      setValue('clients', []);
      return () => reset();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (!dialog.current?.open && state.isOpen) dialog.current?.showModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isOpen]);

  useEffect(
    () => {
      if (state.project)
        setClientsPagination((prev) => ({
          ...prev,
          filters: {
            id: {
              $nin: state.project!.users.map((u) => u.id),
            },
          },
        }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.project],
  );

  return (
    <dialog ref={dialog} className="max-w-xl">
      <h3 className="text-title mr-20">{t('project.addUsersDialog.title')}</h3>
      <form className="grid gap-4" onSubmit={handleSubmit(onSubmit)}>
        {clientsQuery.data && (
          <Multiselect
            label="project.clients"
            options={clientsQuery.data?.users}
            show={(r) => r.name}
            value={clients || []}
            onChange={(v) => setValue('clients', v, { shouldValidate: true })}
            error={formState.errors.clients?.message?.toString()}
          />
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="primary-outline"
            onClick={() => {
              handleClose();
            }}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="primary-fill">
            {t('common.save')}
          </button>
        </div>
      </form>
    </dialog>
  );
}
